import React, { Component } from 'react';
import { string, shape, number, object } from 'prop-types';
// This MultiTouch lib is used for 2-finger panning.
// which prevents user to experience map-scroll trap, while scrolling the page.
// https://github.com/mapbox/mapbox-gl-js/issues/2618
// TODO: we should add an overlay with text "use two fingers to pan".
import MultiTouch from 'mapbox-gl-multitouch';
import uniqueId from 'lodash/uniqueId';

import { circlePolyline } from '../../util/maps';
import { ensureMapboxCss } from '../../util/mapboxLoader';

// Privacy radius (in meters) drawn around the listing location.
const PRIVACY_RADIUS_METERS = 5000; // 5 km
// Extra breathing room so the whole circle stays inside the viewport. Kept
// small (5%) so the map can zoom in as close as possible while still showing
// the entire 5 km privacy circle.
const PRIVACY_RADIUS_MARGIN = 1.05;
// Earth's circumference at the equator (meters), used for the max-zoom calc.
const EARTH_CIRCUMFERENCE_METERS = 40075016.686;

// Viewports at or below this width (px) are treated as mobile.
const MOBILE_BREAKPOINT = 768;

// Zoom level at which the whole 5 km privacy circle (10 km diameter + margin)
// just fits within `viewportPx` at the given latitude. Used both to cap zoom-in
// and to pick the default zoom on mobile.
const fitZoomForCircle = (viewportPx, lat) => {
  const diameterWithMargin = 2 * PRIVACY_RADIUS_METERS * PRIVACY_RADIUS_MARGIN;
  // Meters per pixel at zoom 0 for this latitude (Mapbox uses 512px tiles).
  const metersPerPixelAtZoom0 =
    (EARTH_CIRCUMFERENCE_METERS * Math.cos((lat * Math.PI) / 180)) / 512;
  // meters-per-pixel at zoom z = metersPerPixelAtZoom0 / 2^z. We need the
  // circle's diameter to span at most `viewportPx` pixels.
  const zoom = Math.log2((viewportPx * metersPerPixelAtZoom0) / diameterWithMargin);
  return Math.max(1, Math.min(20, zoom));
};

// Privacy circle styling — matches the static map overlay (muted crimson fill
// with a faint border) instead of a bright solid red.
const DEFAULT_CIRCLE_COLOR = '#c0392b';
const CIRCLE_FILL_OPACITY = 0.2;
const CIRCLE_STROKE_OPACITY = 0.5;
const CIRCLE_STROKE_WIDTH = 1;

const mapMarker = () => {
  return new window.mapboxgl.Marker();
};

// GeoJSON source (a polygon approximating the privacy circle) for the given
// center + radius.
const circleSource = (center, radiusMeters) => {
  const path = circlePolyline(center, radiusMeters).map(([lat, lng]) => [lng, lat]);
  return {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [path],
      },
    },
  };
};

const generateFuzzyLayerId = () => {
  return uniqueId('fuzzy_layer_');
};

class DynamicMapboxMap extends Component {
  constructor(props) {
    super(props);

    this.mapContainer = null;
    this.map = null;
    this.centerMarker = null;
    this.fuzzyLayerId = generateFuzzyLayerId();

    this.updateFuzzyCirclelayer = this.updateFuzzyCirclelayer.bind(this);
    this.updateMaxZoom = this.updateMaxZoom.bind(this);
  }
  componentDidMount() {
    // Load Mapbox GL's stylesheet on demand, now that an interactive map is
    // actually being rendered (it is no longer loaded globally / render-blocking).
    ensureMapboxCss();

    const { center, zoom } = this.props;
    const position = [center.lng, center.lat];

    // On mobile, open at the zoom where the whole 5 km circle just fits the
    // viewport (a tighter default than desktop). Desktop keeps the zoom prop.
    const isMobile =
      typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;
    const viewportPx = this.mapContainer
      ? Math.min(this.mapContainer.offsetWidth, this.mapContainer.offsetHeight)
      : 0;
    const initialZoom =
      isMobile && viewportPx ? fitZoomForCircle(viewportPx, center.lat) : zoom;

    this.map = new window.mapboxgl.Map({
      container: this.mapContainer,
      style: 'mapbox://styles/mapbox/streets-v10',
      center: position,
      zoom: initialZoom,
      // Mouse-wheel zoom stays disabled so the map doesn't trap page scrolling
      // on the (scrollable) listing page. Pinch and double-click zoom are still
      // enabled and are capped by setMaxZoom (see updateMaxZoom).
      scrollZoom: false,
    });
    // Default Mapbox zoom controls (+ / −) — keeps the original map UI.
    this.map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
    this.map.addControl(new MultiTouch());

    this.map.on('load', () => {
      this.addCircleLayers(center);
      // Cap the max zoom so the whole 5 km privacy circle stays visible.
      this.updateMaxZoom();
    });
    // Recompute the cap when the viewport size or center latitude changes.
    this.map.on('resize', this.updateMaxZoom);
    this.map.on('moveend', this.updateMaxZoom);
  }
  componentWillUnmount() {
    if (this.map) {
      this.centerMarker = null;
      this.map.remove();
      this.map = null;
    }
  }
  componentDidUpdate(prevProps) {
    if (!this.map) {
      return;
    }

    const { center, zoom } = this.props;
    const { lat, lng } = center;
    const position = [lng, lat];

    // zoom change
    if (zoom !== prevProps.zoom) {
      this.map.setZoom(this.props.zoom);
    }

    const centerChanged = lat !== prevProps.center.lat || lng !== prevProps.center.lng;

    // circle change
    if (centerChanged) {
      if (this.map.loaded()) {
        this.updateFuzzyCirclelayer();
      } else {
        this.map.on('load', this.updateFuzzyCirclelayer);
      }
    }

    // NOTE: mapsConfig changes are not handled
  }
  updateFuzzyCirclelayer() {
    if (!this.map) {
      // map already removed
      return;
    }
    const { center } = this.props;
    const { lat, lng } = center;
    const position = [lng, lat];

    this.removeCircleLayers();

    // We have to use a different layer id to avoid Mapbox errors
    this.fuzzyLayerId = generateFuzzyLayerId();
    this.addCircleLayers(center);

    this.map.setCenter(position);
  }
  circleColor() {
    return (
      (this.props.mapsConfig &&
        this.props.mapsConfig.fuzzy &&
        this.props.mapsConfig.fuzzy.circleColor) ||
      DEFAULT_CIRCLE_COLOR
    );
  }
  // Add the 5 km privacy circle: a muted crimson fill with a faint border, to
  // match the static map overlay's look.
  addCircleLayers(center) {
    if (!this.map) {
      return;
    }
    const sourceId = this.fuzzyLayerId;
    const fillId = `${this.fuzzyLayerId}_fill`;
    const strokeId = `${this.fuzzyLayerId}_stroke`;
    const color = this.circleColor();

    this.map.addSource(sourceId, circleSource(center, PRIVACY_RADIUS_METERS));
    this.map.addLayer({
      id: fillId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': color,
        'fill-opacity': CIRCLE_FILL_OPACITY,
      },
    });
    this.map.addLayer({
      id: strokeId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': color,
        'line-opacity': CIRCLE_STROKE_OPACITY,
        'line-width': CIRCLE_STROKE_WIDTH,
      },
    });
  }
  removeCircleLayers() {
    if (!this.map) {
      return;
    }
    const sourceId = this.fuzzyLayerId;
    const fillId = `${this.fuzzyLayerId}_fill`;
    const strokeId = `${this.fuzzyLayerId}_stroke`;

    if (this.map.getLayer(strokeId)) this.map.removeLayer(strokeId);
    if (this.map.getLayer(fillId)) this.map.removeLayer(fillId);
    if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
  }
  /**
   * Constrain the map's maximum zoom so the 5 km privacy circle (10 km
   * diameter, plus a small margin) always fits within the smaller viewport
   * dimension. Mapbox's setMaxZoom clamps every zoom-in interaction (pinch,
   * double-click) and disables the "+" NavigationControl button once the limit
   * is reached. Zoom-out stays unrestricted.
   */
  updateMaxZoom() {
    if (!this.map) {
      return;
    }
    const container = this.map.getContainer();
    const viewportPx = Math.min(container.offsetWidth, container.offsetHeight);
    if (!viewportPx) {
      return;
    }

    const clamped = fitZoomForCircle(viewportPx, this.map.getCenter().lat);

    if (Math.abs(this.map.getMaxZoom() - clamped) > 0.01) {
      // setMaxZoom eases the map out if the current zoom exceeds the new max.
      this.map.setMaxZoom(clamped);
    }
  }
  render() {
    const { containerClassName, mapClassName } = this.props;
    return (
      <div className={containerClassName}>
        <div className={mapClassName} ref={el => (this.mapContainer = el)} />
      </div>
    );
  }
}

DynamicMapboxMap.defaultProps = {
  address: '',
  center: null,
};

DynamicMapboxMap.propTypes = {
  address: string, // not used
  center: shape({
    lat: number.isRequired,
    lng: number.isRequired,
  }).isRequired,
  zoom: number.isRequired,
  mapsConfig: object.isRequired,
};

export default DynamicMapboxMap;
